/**
 * External dependencies
 */
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/tabs';
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toggle-group';
import { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

/**
 * Internal dependencies
 */
import { Card, CardContent } from '@Components/Card';
import Breadcrumbs from '@Components/Breadcrumbs';
import Heading from '@Components/Heading';
import Loader from '@Components/Loader';
import { useEngagementStats } from '@Hooks/useEngagementStats';
import {
  OTHER_FLOWS_KEY,
  UNATTRIBUTED_FLOW_KEY,
  type DailyEngagementStats,
  type EngagementStats,
  type FlowSeries,
  type StatsRange,
} from '@Types/messagingStats';
import FirstTouchSection from './FirstTouchSection';
import RangePicker from './RangePicker';
import {
  AXIS_PROPS,
  COLORS,
  FLOW_SERIES_COLORS,
  OTHER_FLOWS_COLOR,
  UNATTRIBUTED_FLOW_COLOR,
  ChartCard,
  StatCard,
  formatAgo,
  formatCount,
  formatDay,
  formatHour,
  formatShare,
} from './shared';

const DEFAULT_RANGE: StatsRange = { days: 7 };
const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** General: one bar per source. Detailed: the Flow source split into one bar per Flow. */
type MixView = 'general' | 'detailed';

const MIX_VIEW_TITLES: Record<MixView, string> = {
  general: 'Daily Outbound SMS by Source',
  detailed: 'Daily Outbound SMS by Source & Flow',
};

/** Legend name and colour of one per-Flow series; `index` is its position in `flowSeries`. */
const flowSeriesStyle = (series: FlowSeries, index: number) => {
  if (series.key === OTHER_FLOWS_KEY) return { name: 'Other flows', fill: OTHER_FLOWS_COLOR };
  if (series.key === UNATTRIBUTED_FLOW_KEY) {
    return { name: 'Unknown flow', fill: UNATTRIBUTED_FLOW_COLOR };
  }
  return {
    name: series.title || 'Untitled flow',
    fill: FLOW_SERIES_COLORS[index % FLOW_SERIES_COLORS.length],
  };
};

function StatCards({ data }: { data: EngagementStats }) {
  const { sent, received, failed, unread } = data;
  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      <StatCard
        label="Messages sent"
        value={formatCount(sent.total)}
        detail={`${sent.dailyAverage.toLocaleString()} per day`}
      />
      <StatCard
        label="Messages received"
        value={formatCount(received.total)}
        detail={`${formatShare(received.afterHoursShare)} after hours`}
      />
      <StatCard
        label="Fish contacted"
        value={formatCount(sent.uniqueRecipients)}
        detail="unique recipients"
      />
      <StatCard
        label="Sent per message received"
        value={data.sentPerReceived ?? '—'}
      />
      <StatCard
        label="Busiest day"
        value={sent.peakDay ? formatCount(sent.peakDay.sent) : '—'}
        detail={sent.peakDay && formatDay(sent.peakDay.date, 'EEE, MMM d')}
      />
      <StatCard
        label="Failed sends"
        value={formatCount(failed.total)}
        detail={`${formatShare(failed.rate, 1)} of attempts`}
      />
      <StatCard
        label="Unread now"
        value={formatCount(unread.total)}
        detail={
          unread.oldestReceivedAt &&
          `oldest ${formatAgo(unread.oldestReceivedAt)}`
        }
      />
      <StatCard
        label="Active senders"
        value={sent.activeSenders}
        detail={`of ${sent.senders} staff with an SMS inbox`}
      />
    </div>
  );
}

function Charts({ data }: { data: EngagementStats }) {
  const { businessHours } = data;
  const [mixView, setMixView] = useState<MixView>('general');
  const splitByHours = (received: number, inHours: boolean) => ({
    inHours: inHours ? received : 0,
    afterHours: inHours ? 0 : received,
  });
  const hours = data.receivedByHour.map((row) => ({
    label: formatHour(row.hour),
    ...splitByHours(
      row.received,
      row.hour >= businessHours.startHour && row.hour < businessHours.endHour,
    ),
  }));
  const weekdays = data.receivedByWeekday.map((row) => ({
    label: WEEKDAY_LABELS[row.weekday - 1],
    ...splitByHours(row.received, businessHours.weekdays.includes(row.weekday)),
  }));

  const grid = (
    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} vertical={false} />
  );
  const yAxis = <YAxis allowDecimals={false} width={40} {...AXIS_PROPS} />;
  const dailyXAxis = (
    <XAxis
      dataKey="date"
      tickFormatter={(date: string) => formatDay(date, 'MMM d')}
      minTickGap={24}
      {...AXIS_PROPS}
    />
  );
  const dailyTooltip = (
    <Tooltip
      cursor={{ fill: COLORS.grid }}
      labelFormatter={(date) => formatDay(String(date), 'EEE, MMM d, yyyy')}
    />
  );
  const receivedBars = [
    <Bar
      key="inHours"
      dataKey="inHours"
      name="Business hours"
      stackId="received"
      fill={COLORS.received}
    />,
    <Bar
      key="afterHours"
      dataKey="afterHours"
      name="After hours"
      stackId="received"
      fill={COLORS.afterHours}
    />,
  ];

  return (
    <>
      <ChartCard title="Daily Messages Sent & Received">
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={data.daily}>
            {grid}
            {dailyXAxis}
            {yAxis}
            {dailyTooltip}
            <Legend />
            <Bar dataKey="sent" name="Sent" fill={COLORS.sent} />
            <Bar dataKey="received" name="Received" fill={COLORS.received} />
            <Line
              dataKey="recipients"
              name="Fish contacted"
              stroke={COLORS.recipients}
              strokeWidth={2}
              dot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>
      <ChartCard
        title={MIX_VIEW_TITLES[mixView]}
        action={
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            value={mixView}
            onValueChange={(value) => value && setMixView(value as MixView)}
            aria-label="Outbound SMS detail level"
          >
            <ToggleGroupItem value="general" className="px-3">
              General
            </ToggleGroupItem>
            <ToggleGroupItem value="detailed" className="px-3">
              Detailed
            </ToggleGroupItem>
          </ToggleGroup>
        }
      >
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data.daily}>
            {grid}
            {dailyXAxis}
            {yAxis}
            {dailyTooltip}
            <Legend />
            <Bar dataKey="sent" name="Staff" stackId="mix" fill={COLORS.sent} />
            {mixView === 'general' ? (
              <Bar dataKey="flow" name="Flow" stackId="mix" fill={COLORS.flow} />
            ) : (
              data.flowSeries.map((series, index) => (
                <Bar
                  key={series.key}
                  dataKey={(day: DailyEngagementStats) => day.flows[series.key] ?? 0}
                  stackId="mix"
                  {...flowSeriesStyle(series, index)}
                />
              ))
            )}
            <Bar
              dataKey="campaign"
              name="Campaign"
              stackId="mix"
              fill={COLORS.campaign}
            />
            <Bar dataKey="other" name="Other" stackId="mix" fill={COLORS.other} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard
          title={`Inbound SMS by Hour · Business Hours ${formatHour(
            businessHours.startHour,
          )}–${formatHour(businessHours.endHour)}`}
        >
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={hours}>
              {grid}
              <XAxis dataKey="label" interval={2} {...AXIS_PROPS} />
              {yAxis}
              <Tooltip cursor={{ fill: COLORS.grid }} />
              {receivedBars}
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Inbound SMS by Day of Week">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={weekdays}>
              {grid}
              <XAxis dataKey="label" {...AXIS_PROPS} />
              {yAxis}
              <Tooltip cursor={{ fill: COLORS.grid }} />
              {receivedBars}
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </>
  );
}

function StaffUsersTable({ data }: { data: EngagementStats }) {
  return (
    <Card>
      <CardContent>
        <div className="mb-2 text-sm font-medium">Messages Sent by Staff Member</div>
        {!data.users.length ? (
          <div className="py-8 text-center text-sm text-zinc-500">
            No staff own an SMS inbox.
          </div>
        ) : (
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                <TableHead>Staff user</TableHead>
                <TableHead className="text-right">Messages sent</TableHead>
                <TableHead className="text-right">Share</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.users.map((user) => (
                <TableRow key={user.userId}>
                  <TableCell>{user.name}</TableCell>
                  <TableCell className="text-right">
                    {formatCount(user.sent)}
                  </TableCell>
                  <TableCell className="text-right text-zinc-500">
                    {formatShare(
                      data.sent.total ? user.sent / data.sent.total : null,
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function Overview({ range }: { range: StatsRange }) {
  const { data, status, error } = useEngagementStats(range);

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-zinc-500">
        Sent counts staff sends, one per recipient; flow and campaign sends are
        counted only under source.
      </p>
      {status === 'pending' ? (
        <Loader size="lg" />
      ) : status === 'error' ? (
        <Card>
          <CardContent>
            <div className="py-8 text-center text-sm text-red-500">
              {error.message}
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <StatCards data={data} />
          <Charts data={data} />
          <StaffUsersTable data={data} />
        </>
      )}
    </div>
  );
}

export default function EngagementStatsPage() {
  const [range, setRange] = useState<StatsRange>(DEFAULT_RANGE);
  const [flowId, setFlowId] = useState<string | null>(null);

  return (
    <div className="w-full">
      <Breadcrumbs
        navList={[
          { name: 'Admin', href: '/engagement-stats' },
          { name: 'Engagement Stats', href: '/engagement-stats' },
        ]}
      />
      <div className="mt-8 mb-4 flex items-start justify-between gap-4">
        <div>
          <Heading>Engagement Stats</Heading>
          <p className="text-sm text-zinc-500">
            SMS activity through staff inboxes.
          </p>
        </div>
        <RangePicker range={range} onChange={setRange} />
      </div>
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="flow-analysis">Flow Analysis</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-6">
          <Overview range={range} />
        </TabsContent>
        <TabsContent value="flow-analysis" className="mt-6">
          <FirstTouchSection
            range={range}
            flowId={flowId}
            onFlowChange={setFlowId}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
